import "./SealLandingPage.scss";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../../paths";
import sealUsageImage from "../../../images/seal/seal-usage.jpg";
import sealBottleImage from "../../../images/seal/seal-bottle.jpg";

export const SealLandingPage = () => {
  const navigate = useNavigate();

  return (
    <div>
      <AppSeo
        title="The OVR Seal"
        description="The OVR Seal certifies wines made from verified old-vine vineyards listed in the Old Vine Registry."
        keywords="OVR seal, old vine seal, old vine certification, old vine wine, old vine registry seal"
      />
      <div className="about-layout">
        <div>
          <div className="seal-landing-label u-mt-large">OVR SEAL PROGRAM</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <h1 className="title-large" style={{ margin: 0 }}>The OVR Seal</h1>
            </div>
          </div>
          <div className="seal-landing-image-wrap">
            <img
              src={sealUsageImage}
              alt="The OVR Seal"
              className="seal-landing-image"
            />
          </div>
          <div className="seal-landing-cta">
            <button
              className="button"
              onClick={() => navigate(Paths.sealRegister)}
            >
              Register a Brand or Business
            </button>
          </div>

          <p className="description-text u-mt-large" style={{ fontStyle: "italic" }}>
            Help consumers recognize wines made from the world's old-vine
            vineyards by using the Old Vine Registry Seal.
          </p>
          <p className="description-text">
            The OVR Seal identifies wines made with grapes from old-vine
            vineyards registered in the Old Vine Registry. It gives wine
            producers and trade organizations a consistent way to communicate a
            wine's connection to a documented vineyard and its heritage.
          </p>
          <p className="description-text">
            Registered organizations may use the seal in approved contexts,
            including eligible wine packaging, e-commerce listings, websites,
            and other marketing materials.
          </p>

          <div className="sub-headline u-mt-large">Which wines qualify?</div>
          <p className="description-text">
            A wine may use an OVR Seal when at least 85% of its grapes come from
            one or more vineyards registered in the Old Vine Registry.
          </p>
          <p className="description-text">
            To use a particular age seal, at least 85% of the wine's grapes must
            come from registered vineyards that meet or exceed the age shown on
            the seal.
          </p>
          <p className="description-text">OVR Seals are available for:</p>
          <ul className="description-text seal-landing-age-list">
            <li>35+ years</li>
            <li>50+ years</li>
            <li>75+ years</li>
            <li>100+ years</li>
            <li>150+ years</li>
          </ul>
          <p className="description-text">
            Each vineyard's age is calculated from the planting date shown in its
            OVR record. The record also indicates whether that date is exact or
            estimated and, when applicable, explains how the estimate was
            determined.
          </p>

          <div className="sub-headline u-mt-large">An example</div>
          <p className="description-text">
            A wine made with 60% fruit from a registered 100-year-old vineyard
            and 25% from a registered 75-year-old vineyard may use the 75+ seal.
            It may not use the 100+ seal because less than 85% of the wine meets
            that age threshold.
          </p>

          <div className="sub-headline u-mt-large">Who can register?</div>
          <p className="description-text">
            The OVR Seal program is open to:
          </p>
          <ul className="description-text seal-landing-bullet-list">
            <li>Vineyard owners and operators</li>
            <li>Wine producers</li>
            <li>Wine brand owners</li>
            <li>Importers</li>
            <li>Retailers</li>
          </ul>
          <p className="description-text">
            Not sure whether your organization is eligible?{" "}
            <a
              className="description-text-link"
              href="mailto:oldvineregistry@gmail.com"
            >
              Contact OVR
            </a>
            .
          </p>

          <div className="seal-landing-info-box u-mt-medium">
            <p className="description-text">
              <strong>South African Exception</strong>
            </p>
            <p className="description-text">
              The OVR Seal is not issued to or valid for use by South African vineyards or wine producers under any circumstances. This is because South Africa already has in place their <a href="https://www.oldvineproject.co.za/aboutus/certifiedheritagevineyardseal">Certified Heritage Vineyard</a> program developed by the <a href="https://www.oldvineproject.co.za">Old Vine Project</a>.  Interested producers from South Africa should engage with the <a href="https://www.oldvineproject.co.za">Old Vine Project</a> and utilize the CHV initiative in lieu of the OVR Seal.
            </p>
          </div>

          <div className="sub-headline u-mt-large">
            What does one registration cover?
          </div>
          <p className="description-text">
            Each US $300 registration covers one named participant in the OVR
            Seal program. The named participant may be:
          </p>
          <ul className="description-text seal-landing-bullet-list">
            <li>One consumer-facing wine brand</li>
            <li>One vineyard business</li>
            <li>One importing business</li>
            <li>One retail business</li>
          </ul>
          <p className="description-text">
            <strong>
              One registration does not cover an entire portfolio of separately
              marketed wine brands.
            </strong>
          </p>
          <p className="description-text">
            Additional wine brands require separate registrations, even when they
            share the same parent company, legal owner, producer, importer, or
            management.
          </p>
          <p className="description-text">
            A registered wine brand may use the appropriate OVR Seal for all of
            its eligible wines and vintages. Individual wines and vintages do not
            require separate registrations.
          </p>
          <div className="seal-landing-info-box u-mt-medium">
            <p className="description-text">
              <strong>Example</strong>
            </p>
            <p className="description-text">
              A producer with one wine brand and several eligible old-vine wines
              needs one registration. A company wishing to use the seal across
              five separately marketed wine brands needs five registrations.
            </p>
          </div>

          <div className="seal-landing-image-wrap u-mt-large">
            <img
              src={sealBottleImage}
              alt="OVR Seal on a wine bottle"
              className="seal-landing-image"
            />
          </div>

          <div className="sub-headline u-mt-large">
            Using the seal on wine packaging
          </div>
          <p className="description-text">
            To use the OVR Seal on a bottle, label, closure, carton, case, or
            other wine packaging, the consumer-facing wine brand shown on that
            packaging must be registered.
          </p>
          <p className="description-text">
            The applicant must be the wine producer, the wine brand owner, or
            another eligible organization authorized to control packaging for
            that brand.
          </p>
          <p className="description-text">
            Registration for a parent company, importer, retailer, vineyard
            business, or another wine brand does not authorize packaging use by
            an unregistered brand.
          </p>

          <div className="sub-headline u-mt-large">
            Using the seal in sales and retail channels
          </div>
          <p className="description-text">
            Registered importers and retailers may display the OVR Seal beside
            eligible wines on their own websites, e-commerce listings, catalogs,
            wine lists, shelf talkers, and promotional materials.
          </p>
          <p className="description-text">
            Their registration applies only to their own communications. It does
            not register the wine brands they carry and does not authorize those
            brands or producers to place the seal on wine packaging.
          </p>
          <p className="description-text">
            Importers and retailers may not transfer or provide the artwork to
            another organization for its independent use.
          </p>

          <div className="sub-headline u-mt-large">
            Using the seal for a registered vineyard
          </div>
          <p className="description-text">
            A registered vineyard business may use the appropriate seal in
            connection with its registered vineyard, including on its website,
            promotional materials, and vineyard signage.
          </p>
          <p className="description-text">
            Vineyard registration does not authorize an unregistered wine brand
            to use the seal on its packaging.
          </p>

          <div className="sub-headline u-mt-large">
            What registration includes
          </div>
          <p className="description-text">Registration provides:</p>
          <ul className="description-text seal-landing-bullet-list">
            <li>
              Authorization for one named wine brand, vineyard business,
              importer, or retailer
            </li>
            <li>
              Official OVR Seal artwork appropriate to the participant's
              permitted uses
            </li>
            <li>Access to all available age badges</li>
            <li>
              Packaging, digital, and promotional usage guidelines
            </li>
            <li>
              Use of the appropriate seal for all eligible wines associated with
              the registered name
            </li>
            <li>Recognition as an authorized OVR Seal participant</li>
          </ul>

          <div className="sub-headline u-mt-large">Before you register</div>
          <p className="description-text">You will need:</p>
          <ul className="description-text seal-landing-bullet-list">
            <li>
              The legal name of the organization responsible for the
              registration
            </li>
            <li>
              The name of the wine brand, vineyard business, importing business,
              or retail business being registered
            </li>
            <li>Your organization's role or roles</li>
            <li>Your intended uses of the seal</li>
            <li>At least one relevant Old Vine Registry vineyard page</li>
            <li>
              Authority to register on behalf of the named participant
            </li>
            <li>
              A payment method for the US $300 one-time registration fee
            </li>
          </ul>
          <p className="description-text">
            If you represent a company with several wine brands, submit a
            separate registration for each brand that will use the seal.
          </p>
          <p className="description-text">
            As part of registration, you will attest that the named participant
            will:
          </p>
          <ul className="description-text seal-landing-bullet-list">
            <li>Use the seal only on qualifying wines</li>
            <li>Select the correct age seal</li>
            <li>Follow the OVR Seal Usage Guidelines</li>
            <li>Use the seal only in ways permitted for its role</li>
            <li>
              Not give, transfer, or sublicense the seal artwork to another
              organization
            </li>
          </ul>
          <p className="description-text">
            Don't see your vineyard in the Registry?{" "}
            <span
              className="description-text-link"
              onClick={() => navigate(Paths.submit)}
            >
              Submit a vineyard for inclusion in the OVR
            </span>{" "}
            before registering for the seal program.
          </p>

          <div className="sub-headline u-mt-large">Registration fee</div>
          <p className="description-text">
            Registration costs US $300 per registered wine brand, vineyard
            business, importer, or retailer.
          </p>
          <p className="description-text">
            The registration fee is paid once for each named participant. There
            are no annual renewal fees.
          </p>
          <p className="description-text">
            Additional wine brands require separate registrations.
          </p>

          <div className="sub-headline u-mt-large">
            Important program limitations
          </div>
          <p className="description-text">
            The registered participant is responsible for ensuring that every use
            of the seal meets the vineyard-source, age, artwork, and
            role-specific requirements of the OVR Seal program.
          </p>
          <p className="description-text">
            Registration is non-transferable. Seal artwork may not be given,
            sublicensed, or otherwise provided to another wine brand or
            organization for its independent use.
          </p>
          <p className="description-text">
            The OVR Seal does not certify wine quality, sustainability, farming
            practices, or production methods. The Old Vine Registry makes best
            efforts to verify the claims and eligibility of seal registrants, but
            ultimately makes no warranty as to the veracity of any registrant's
            claims in conjunction with their use of the OVR Seal.
          </p>
          <p className="submit-error-heading">
            All producers are responsible for checking whether use of the seal on labels and other packaging materials is in compliance with the labelling laws and guidelines from their relevant regulatory body before purchase.
          </p>
          <p className="description-text">
            Registration must be confirmed by OVR before the participant begins
            using the seal.
          </p>
          <div className="seal-landing-cta u-mt-large">
            <p className="description-text" style={{ textAlign: "center" }}>
              One registration covers one named wine brand, vineyard business,
              importer, or retailer.
            </p>
            <button
              className="button"
              onClick={() => navigate(Paths.sealRegister)}
            >
              Register a Brand or Business
            </button>
          </div>

          <div className="seal-landing-footer-links u-mt-large">
            <span
              className="description-text-link"
              onClick={() => navigate(Paths.sealGuidelines)}
            >
              Read the OVR Seal Usage Guidelines
            </span>
            <span className="seal-landing-footer-separator">&middot;</span>
            <span
              className="description-text-link"
              onClick={() => navigate(Paths.advancedSearch)}
            >
              Search for a vineyard in the OVR
            </span>
            <span className="seal-landing-footer-separator">&middot;</span>
            <span
              className="description-text-link"
              onClick={() => navigate(Paths.sealFaq)}
            >
              View the OVR Seal FAQs
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
